import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import SearchResults from "_shared/_api/search/search-results";
import { assertIsAxiosError } from "_shared/_errors/errors";
import Guid from "_shared/guids/guid";
import axios, { AxiosHeaders, CanceledError, HttpStatusCode } from "axios";
import Individual from "marketplace/individuals/individual";
import MarketplaceTeamInfoAPIResponse from "marketplace/team-profile/_api/response-contracts/marketplace-team-info-api-response";
import MarketplaceTeamAPIRequest from "marketplace/teams/_api/request-contracts/marketplace-team-api-request";
import MarketplaceTeamSearch from "marketplace/teams/_api/request-contracts/marketplace-team-search";
import UpdateMarketplaceTeamAPIRequest from "marketplace/teams/_api/request-contracts/update-marketplace-team-api-request";
import TeamSchema, { TeamJSON } from "marketplace/teams/_schema/team-schema";
import TeamInvitationCommand from "marketplace/teams/invitations/_api/commands/team-invitation-command";
import TeamInvitationSchema, {
  TeamInvitationJSON,
} from "marketplace/teams/invitations/_schemas/team-invitation/team-invitation-schema";
import Team from "marketplace/teams/team";
import ForumSchema from "messaging/forums/_schemas/forum-schema";
import Forum from "messaging/forums/forum";
import Session from "users/__session/session";

export default class MarketplaceTeamAPIService {
  public constructor(params: { session: Readonly<Session>; abortController?: AbortController }) {
    if (!params.session.authToken) {
      throw new Error("Cannot create MarketplaceTeamAPIService without session.");
    }
    this.session = params.session;
    this.abortController = params.abortController;
    this.headers.set("Authorization", `Bearer ${params.session.authToken.value}`);
  }

  public async getTeamVendors(): Promise<TeamJSON[]> {
    const url = new URL("/marketplace/team-vendors", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: this.abortController?.signal,
      });
      return TeamSchema.validateJSONArray(response.data);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      throw new MarketplaceTeamServiceError("getTeamVendors", error);
    }
  }

  public async searchTeams(search: MarketplaceTeamSearch): Promise<SearchResults<MarketplaceTeamInfoAPIResponse>> {
    let params = new URLSearchParams();
    for (const [key, value] of Object.entries(search)) {
      if (value) {
        params.append(key, value.toString());
      }
    }
    const url = new URL(`/marketplace/teams?${params.toString()}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: this.abortController?.signal,
      });
      return new SearchResults<MarketplaceTeamInfoAPIResponse>(response.data);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      throw new MarketplaceTeamServiceError("searchTeams", error);
    }
  }

  public async getTeamById(teamId: Guid): Promise<TeamJSON> {
    const url = new URL(`/marketplace/teams/${teamId.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: this.abortController?.signal,
      });
      return TeamSchema.validateJSON(response.data);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      assertIsAxiosError(error);
      switch (error.response?.status) {
        case HttpStatusCode.NotFound:
          throw new MarketplaceTeamNotFoundError("Requested vendor-team was not found");
        case HttpStatusCode.Forbidden:
          throw new HiddenMarketplaceTeamError("Requested vendor-team is not visible");
        default:
          throw new MarketplaceTeamServiceError("getTeamById", error);
      }
    }
  }

  public async getMarketplaceTeamsByIds(ids: Guid[], abortController?: AbortController): Promise<TeamJSON[]> {
    const url = new URL("/marketplace/teams-by-ids", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        ids.map((id) => id.value),
        {
          headers: this.headers.concat({ "Content-Type": "application/json-patch+json" }),
          signal: abortController?.signal,
        },
      );
      return TeamSchema.validateJSONArray(response.data);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      assertIsAxiosError(error);
      throw new MarketplaceTeamServiceError("getMarketplaceTeamsByIds", error);
    }
  }

  public async getTeamAvatar(teamId: Guid, abortController?: AbortController): Promise<string> {
    const url = new URL(`/marketplace/teams/${teamId.value}/avatar`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: abortController?.signal,
      });
      return response.data;
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      assertIsAxiosError(error);
      switch (error.response?.status) {
        case HttpStatusCode.NotFound:
          throw new MarketplaceTeamAvatarNotFoundError(String(error.response.data));
        case HttpStatusCode.Forbidden:
          throw new HiddenMarketplaceTeamError("Requested vendor-team is not visible");
        default:
          throw new MarketplaceTeamServiceError("getTeamAvatar", error);
      }
    }
  }

  public async getTeamInvitationByInvitationId(invitationId: Guid): Promise<TeamInvitationJSON> {
    const url = new URL(`/marketplace/teams/invitations/${invitationId.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: this.abortController?.signal,
      });
      return TeamInvitationSchema.validateJSON(response.data);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      assertIsAxiosError(error);
      switch (error.response?.status) {
        case HttpStatusCode.NotFound:
          throw new MarketplaceTeamInvitationNotFoundError("Requested vendor-team invitation was not found");
        case HttpStatusCode.Forbidden:
          throw new MarketplaceTeamInvitationError("Caller does not have permission to view this invitation");
        default:
          throw new MarketplaceTeamServiceError("getTeamInvitationByInvitationId", error);
      }
    }
  }

  public async getTeamInvitationsByTeamId(teamId: Guid, open: boolean = true): Promise<TeamInvitationJSON[]> {
    const url = new URL(
      `/marketplace/teams/invitations?teamId=${teamId.value}&open=${open}`,
      AttorneyHubAPIService.apiBaseUrl,
    );
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: this.abortController?.signal,
      });
      return TeamInvitationSchema.validateJSONArray(response.data);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      assertIsAxiosError(error);
      switch (error.response?.status) {
        case HttpStatusCode.NotFound:
          throw new MarketplaceTeamInvitationError("Requested vendor-team invitations were not found");
        case HttpStatusCode.Forbidden:
          throw new MarketplaceTeamInvitationError("Caller does not have permission to view these invitations");
        default:
          throw new MarketplaceTeamServiceError("getTeamInvitationsByTeamId", error);
      }
    }
  }

  public async createTeam(team: Team): Promise<TeamJSON> {
    const url = new URL("/marketplace/teams", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(url.toString(), new MarketplaceTeamAPIRequest(team), {
        headers: this.headers.concat({ "Content-Type": "application/json-patch+json" }),
      });
      return TeamSchema.validateJSON(response.data);
    } catch (error) {
      assertIsAxiosError(error);
      switch (error.response?.status) {
        case HttpStatusCode.BadRequest:
          throw new MarketplaceTeamServiceError("createTeam", error);
        case HttpStatusCode.Forbidden:
          throw new MarketplaceTeamServiceError("createTeam", "User not authorized to create this vendor-team");
        default:
          throw new MarketplaceTeamServiceError("createTeam", error);
      }
    }
  }

  public async updateTeam(originalTeam: Team, updatedTeam: Team): Promise<TeamJSON> {
    if (!originalTeam.id) throw new Error("Original vendor-team must have an id to update");
    const url = new URL(`/marketplace/teams/${originalTeam.id.value}`, AttorneyHubAPIService.apiBaseUrl);
    const request = new UpdateMarketplaceTeamAPIRequest(originalTeam, updatedTeam);
    if (!request.payload.length) {
      console.warn("No changes detected between original and updated vendor-team. Skipping update.");
      return updatedTeam.toJSON();
    }
    try {
      const response = await axios.patch(
        url.toString(),
        new UpdateMarketplaceTeamAPIRequest(originalTeam, updatedTeam).payload,
        {
          headers: this.headers.concat({ "Content-Type": "application/json-patch+json" }),
        },
      );
      return TeamSchema.validateJSON(response.data);
    } catch (error) {
      assertIsAxiosError(error);
      switch (error.response?.status) {
        case HttpStatusCode.BadRequest:
          throw new MarketplaceTeamUpdateError("Unable to update the vendor-team");
        case HttpStatusCode.NotFound:
          throw new MarketplaceTeamServiceError("updateTeam", "Requested vendor-team was not found");
        default:
          throw new MarketplaceTeamServiceError("updateTeam", error);
      }
    }
  }

  public async deleteTeam(teamId: Guid): Promise<void> {
    const url = new URL(`/marketplace/teams/${teamId.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.delete(url.toString(), {
        headers: this.headers,
      });
    } catch (error) {
      assertIsAxiosError(error);
      switch (error.response?.status) {
        case HttpStatusCode.NotFound:
          throw new MarketplaceTeamNotFoundError("Requested vendor-team was not found");
        case HttpStatusCode.Forbidden:
          throw new MarketplaceTeamServiceError("deleteTeam", "User not authorized to delete this vendor-team");
        default:
          throw new MarketplaceTeamServiceError("deleteTeam", error);
      }
    }
  }

  /** Returns base64 encoded avatar image */
  public async createTeamAvatar(teamId: Guid, file: File): Promise<string> {
    const url = new URL(`/marketplace/teams/${teamId.value}/avatar`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const formData = new FormData();
      formData.append("file", file);
      return await axios.post(url.toString(), formData, {
        headers: this.headers.concat({ "Content-Type": "multipart/form-data" }),
      });
    } catch (error) {
      assertIsAxiosError(error);
      switch (error.response?.status) {
        case HttpStatusCode.NotFound:
          throw new MarketplaceTeamNotFoundError("Requested vendor-team was not found");
        case HttpStatusCode.UnprocessableEntity:
          throw new MarketplaceTeamAvatarError("Unable to upload avatar");
        default:
          throw new MarketplaceTeamServiceError("createTeamAvatar", error);
      }
    }
  }

  public async deleteTeamAvatar(teamId: Guid): Promise<void> {
    const url = new URL(`/marketplace/teams/${teamId.value}/avatar`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.delete(url.toString(), {
        headers: this.headers,
      });
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new MarketplaceTeamNotFoundError("Requested vendor-team was not found");
      throw new MarketplaceTeamServiceError("deleteTeamAvatar", error);
    }
  }

  public async requestToJoinTeam(teamId: Guid): Promise<void> {
    const url = new URL(`/marketplace/teams/${teamId.value}/request-to-join`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.post(
        url.toString(),
        {},
        {
          headers: this.headers,
        },
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new MarketplaceTeamNotFoundError("Requested vendor-team to join was not found");
      throw new MarketplaceTeamServiceError("requestToJoinTeam", error);
    }
  }

  public async promoteTeamMemberToLeader(teamId: Guid, individual: Individual): Promise<void> {
    const url = new URL(`/marketplace/teams/${teamId.value}/leader`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.put(
        url.toString(),
        {
          individualId: individual.id.value,
        },
        {
          headers: this.headers,
        },
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new MarketplaceTeamNotFoundError("Requested vendor-team was not found");
      throw new MarketplaceTeamServiceError("promoteTeamMember", error);
    }
  }

  public async removeTeamMember(teamId: Guid, memberIndividualId: Guid): Promise<void> {
    const url = new URL(
      `/marketplace/teams/${teamId.value}/members/remove-by-individual-id`,
      AttorneyHubAPIService.apiBaseUrl,
    );
    try {
      await axios.post(
        url.toString(),
        {
          individualId: memberIndividualId.value,
        },
        {
          headers: this.headers,
        },
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new MarketplaceTeamNotFoundError("Requested vendor-team was not found");
      throw new MarketplaceTeamServiceError("removeTeamMember", error);
    }
  }

  public async cancelTeamInvitation(invitationId: Guid): Promise<void> {
    const url = new URL(
      `/marketplace/teams/invitations/${invitationId.value}/cancel`,
      AttorneyHubAPIService.apiBaseUrl,
    );
    try {
      await axios.post(
        url.toString(),
        {},
        {
          headers: this.headers,
        },
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new MarketplaceTeamInvitationNotFoundError("Requested vendor-team invitation was not found");
      throw new MarketplaceTeamServiceError("cancelTeamInvitation", error);
    }
  }

  public async acceptTeamInvitation(invitationId: Guid): Promise<void> {
    const url = new URL(
      `/marketplace/teams/invitations/${invitationId.value}/accept`,
      AttorneyHubAPIService.apiBaseUrl,
    );
    try {
      await axios.post(
        url.toString(),
        {},
        {
          headers: this.headers,
        },
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new MarketplaceTeamInvitationNotFoundError("Requested vendor-team invitation was not found");
      throw new MarketplaceTeamServiceError("acceptTeamInvitation", error);
    }
  }

  public async rejectTeamInvitation(invitationId: Guid): Promise<void> {
    const url = new URL(
      `/marketplace/teams/invitations/${invitationId.value}/reject`,
      AttorneyHubAPIService.apiBaseUrl,
    );
    try {
      await axios.post(
        url.toString(),
        {},
        {
          headers: this.headers,
        },
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new MarketplaceTeamInvitationNotFoundError("Requested vendor-team invitation was not found");
      throw new MarketplaceTeamServiceError("rejectTeamInvitation", error);
    }
  }

  public async createTeamInvitation(inviteeId: Guid, teamId: Guid, message?: string): Promise<TeamInvitationJSON> {
    const url = new URL("/marketplace/teams/invitations", AttorneyHubAPIService.apiBaseUrl);
    const request = new TeamInvitationCommand();
    request.inviteeId = inviteeId.value;
    request.teamId = teamId.value;
    request.message = message;
    try {
      const response = await axios.post(url.toString(), request, {
        headers: this.headers.concat({ "Content-Type": "application/json-patch+json" }),
      });
      return TeamInvitationSchema.validateJSON(response.data);
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new MarketplaceTeamNotFoundError("Requested vendor-team invitation was not found");
      throw new MarketplaceTeamServiceError("createTeamInvitation", error);
    }
  }

  public async contactTeam(teamId: Guid): Promise<Forum> {
    const url = new URL(`/marketplace/teams/${teamId.value}/contact`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.headers,
        },
      );
      return new Forum(ForumSchema.deserialize(response.data));
    } catch (error) {
      assertIsAxiosError(error);
      switch (error.response?.status) {
        case HttpStatusCode.BadRequest:
          throw new MarketplaceTeamServiceError("contactTeam", "Unable to contact vendor-team");
        case HttpStatusCode.NotFound:
          throw new MarketplaceTeamNotFoundError("Requested vendor-team was not found");
        default:
          throw new MarketplaceTeamServiceError("contactTeam", error);
      }
    }
  }

  private readonly headers: AxiosHeaders = new AxiosHeaders();
  private readonly session: Readonly<Session>;
  private readonly abortController?: AbortController;
}

export class MarketplaceTeamServiceError extends Error {
  public methodName: string;
  public error: unknown;

  public constructor(method: string, error: unknown) {
    super(String(error));

    this.error = error;
    this.methodName = method;
  }
}
export class MarketplaceTeamNotFoundError extends Error {}
export class MarketplaceTeamInvitationError extends Error {}
export class MarketplaceTeamInvitationNotFoundError extends Error {}
export class HiddenMarketplaceTeamError extends Error {}
export class MarketplaceTeamAvatarError extends Error {}
export class MarketplaceTeamUpdateError extends Error {}
export class MarketplaceTeamAvatarNotFoundError extends Error {}
